import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Subscription, map } from 'rxjs';
import { CiMappingService } from '../ci-mapping/ci-mapping.service';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { KiMarkComponent } from '../../components/ki/ki-mark/ki-mark.component';
import {
  KiPipelineUrsacheComponent,
  KiPipelineVerknuepfterBug,
} from '../../components/ki/ki-pipeline-ursache/ki-pipeline-ursache.component';
import { LiveReconnectHintComponent } from '../../components/live-reconnect-hint/live-reconnect-hint.component';
import { ResPillComponent, ResPillStatus } from '../../components/res-pill/res-pill.component';
import { RunnerJobCardComponent } from '../../components/runner-job-card/runner-job-card.component';
import { PipeTrackCell, PipeTrackComponent } from '../../components/pipe-track/pipe-track.component';
import { istBekannteKategorie } from '../../components/pipe-kategorie-pill/pipe-kategorie-pill.component';
import { ReportTestTableComponent } from '../../components/report-test-table/report-test-table.component';
import { ProgressBarComponent } from '../../components/progress-bar/progress-bar.component';
import { buildPipeTrackCells, buildPipeTrackSummary } from '../../components/pipe-track/pipe-track-cells';
import { KiPipelineAktionVorlage } from '../../models/ki.model';
import { PipelineProgress } from '../../models/live-pipeline.model';
import {
  CreateBugFromStepResultRequest,
  PipelineFehlerKategorie,
  ResultStatus,
  RunnerCase,
  RunnerJob,
  RunnerPipelineStep,
  RunnerState,
  TestRunExecution,
  TestRunSource,
} from '../../models/test-run.model';
import { ExecutionJobStatus, isFinalStatus } from '../../services/execution-job.service';
import { CaseResultBugRun, caseResultBugFlow, executionBugFlow } from '../../services/case-result-bug-flow';
import { StepBugLinkService } from '../../services/step-bug-link.service';
import { TestRunService } from '../../services/test-run.service';
import { TestlaufRunnerService } from '../../services/testlauf-runner.service';
import {
  ReportTestLiveCounts,
  TestRunLiveEvent,
  TestlaufLiveFeedService,
} from '../../services/testlauf-live-feed.service';
import { formatDurationMs, formatElapsedClock } from '../../shared/duration.util';
import { isOpenStatus } from '../../shared/test-run-outcome';
import { LiveBugDialogComponent, LiveBugKiAnalyse } from './live-bug-dialog/live-bug-dialog.component';
import { PipelineActionError, pipelineActionError } from './pipeline-action-error';
import { runnerPillStatus, runnerResultKey } from './runner-status';
import { decidedSteps } from './runner-steps';
import { RunnerCaseListComponent } from './runner-case-list/runner-case-list.component';

/** Ein Fall des Durchlaufs, wie Rollup und Fehler-Karte ihn zaehlen. */
interface LiveRow {
  caseResultId: string;
  autoId: string;
  name: string;
  status: ResultStatus;
  /** Wann das Ergebnis gemeldet wurde; {@code null}, solange der Test offen ist. */
  at: string | null;
  /** Der am Fall-Ergebnis verknuepfte Bug-Vorgang; {@code null}, solange keiner haengt. */
  linkedBugKey: string | null;
}

/** Anbieter-Kachel eines CI-Laufs. */
interface PipelineProvider {
  /** Klassen des Marken-Symbols; {@code null}, wo die Marke unbekannt ist. */
  brandIcon: string | null;
  /** Faerbt den Grund der Kachel. */
  tone: string;
  label: string;
}

/** Die Karte der verknuepften Pipeline, fertig zum Zeichnen. */
interface PipelineCard {
  provider: PipelineProvider;
  label: string;
  runUrl: string;
  logUrl?: string;
}

/** Zustand der verknuepften Pipeline als Pille. */
interface PipelinePill {
  status: ResPillStatus;
  label: string;
  pulsing: boolean;
}

interface Rollup {
  total: number;
  passed: number;
  failed: number;
  skipped: number;
  blocked: number;
  notRun: number;
  open: number;
  done: number;
}

/** Ein Teil der Zusammenfassungszeile; {@code strong} hebt die Fehlschlaege hervor. */
interface SummaryPart {
  key: string;
  params: Record<string, string>;
  strong: boolean;
}

/** Die Anbieter, deren Marke die Karte zeichnen kann. */
const PIPELINE_PROVIDERS: Record<string, PipelineProvider> = {
  GITHUB: { brandIcon: 'fa-brands fa-github', tone: 'github', label: 'testRunner.live.pipeline.provider.GITHUB' },
};

/**
 * Der Anbieter, den die Karte nicht kennt: neutrales Symbol auf neutralem Grund.
 * Eine geratene Marke waere eine Falschaussage, eine fehlende Kachel eine Luecke
 * an der Stelle, an der das Design den Anbieter fuehrt.
 */
const UNKNOWN_PROVIDER: PipelineProvider = {
  brandIcon: null,
  tone: 'unknown',
  label: 'testRunner.live.pipeline.provider.UNKNOWN',
};

/**
 * Wofuer die empfohlene Aktion den Bug am Durchlauf anlegt: Kategorien, in denen keine Tests
 * gescheitert sind. Ein Testfehler bleibt beim Fall, ein Abbruch bekommt keinen Bug.
 */
const BUG_AM_DURCHLAUF: ReadonlySet<PipelineFehlerKategorie> = new Set<PipelineFehlerKategorie>([
  'BUILD',
  'ABHAENGIGKEIT',
  'INFRASTRUKTUR',
  'KONFIGURATION',
  'ERGEBNIS_FEHLT',
]);

/** Ausgaenge eines beendeten Durchlaufs, die die Pipeline-Pille auch ohne rote Tests rot faerben (NEX-1626). */
const GESCHEITERT: ReadonlySet<ResultStatus> = new Set<ResultStatus>(['FAILED', 'BLOCKED']);

/** Die Zahlen-Abfrage braucht nur die Zaehler; eine Zeile ist das Mindeste, das der Server annimmt. */
const COUNTS_PAGE_SIZE = 1;

/** Taktung der mitlaufenden Uhr in der Kopfzeile. */
const CLOCK_TICK_MS = 1_000;

let nextMatchingRuleId = 0;

function isFinal(status: ResultStatus): boolean {
  return !isOpenStatus(status);
}

/**
 * Der Meldezeitpunkt als Zahl. Ein abgeschlossener Fall ohne {@code completedAt}
 * (Altbestand von vor NEX-1041) faellt ans Ende statt die Sortierung mit
 * {@code NaN} in eine beliebige Reihenfolge zu kippen.
 */
function reportedAt(row: LiveRow): number {
  const parsed = Date.parse(row.at ?? '');
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Fertige Tests zuerst, das zuletzt gemeldete oben; die offenen behalten darunter
 * ihre Reihenfolge aus dem Lauf. Die Fehler-Karte zeigt so den juengsten Fehler zuerst.
 */
function feedOrder(rows: LiveRow[]): LiveRow[] {
  const done = rows.filter((row) => isFinal(row.status));
  const open = rows.filter((row) => !isFinal(row.status));
  done.sort((left, right) => reportedAt(right) - reportedAt(left));
  return [...done, ...open];
}

function toRow(runnerCase: RunnerCase): LiveRow {
  return {
    caseResultId: runnerCase.id,
    autoId: runnerCase.key ?? runnerCase.id,
    name: runnerCase.title ?? '',
    status: runnerCase.status,
    at: runnerCase.completedAt,
    linkedBugKey: runnerCase.linkedBugKey ?? null,
  };
}

/** Der Wechsel des Auftrags samt Zeitpunkt, damit die Falldauer schon live stimmt. */
function withJobStatus(job: RunnerJob, status: ExecutionJobStatus, at: string): RunnerJob {
  if (status === 'RUNNING') return { ...job, status, startedAt: job.startedAt ?? at };
  if (isFinalStatus(status)) return { ...job, status, finishedAt: job.finishedAt ?? at };
  return { ...job, status };
}

/** Ein Live-Stand des Auftrags, wie `runner-job-updated` ihn meldet. */
interface LiveJobStatus {
  status: ExecutionJobStatus;
  at: string;
}

function jobProgress(status: ExecutionJobStatus): number {
  if (status === 'QUEUED') return 0;
  return status === 'RUNNING' ? 1 : 2;
}

/**
 * Legt den live gemeldeten Auftragsstand ueber den Fall. Ein Zustand, der schon so weit oder
 * weiter ist, gewinnt; ein aelterer (Antwort auf ein Neuladen, die das Ereignis ueberholt hat)
 * verliert nichts. Fehlt der Auftrag am Fall, steht er mit dem Stand aus dem Ereignis da, bis das
 * Neuladen ihn vollstaendig liefert.
 */
function withLiveJob(runnerCase: RunnerCase, live: LiveJobStatus | undefined): RunnerCase {
  const job = runnerCase.runnerJob;
  if (!live || (job && jobProgress(job.status) >= jobProgress(live.status))) {
    return runnerCase;
  }
  const base: RunnerJob = job ?? {
    jobId: '',
    status: 'QUEUED',
    runnerName: null,
    error: null,
    startedAt: null,
    finishedAt: null,
  };
  return { ...runnerCase, runnerJob: withJobStatus(base, live.status, live.at) };
}

/**
 * Live-Pipeline-Ansicht eines automatisierten Durchlaufs (NEX-1043).
 *
 * <p>Kopf, Zusammenfassung und Rollup zaehlen die Report-Tests, sobald es welche gibt,
 * sonst die Faelle aus dem Zustand des Durchlaufs; die Fehler-Karte zaehlt die Faelle.
 * Beides fuehrt der Feed nach; die Tests der Pipeline zeigt die Report-Tabelle.
 */
@Component({
  selector: 'app-testlauf-runner-live',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    MatButtonModule,
    MatIconModule,
    TranslatePipe,
    RouterLink,
    ConfirmDialogComponent,
    KiMarkComponent,
    KiPipelineUrsacheComponent,
    LiveReconnectHintComponent,
    ResPillComponent,
    RunnerJobCardComponent,
    PipeTrackComponent,
    ProgressBarComponent,
    LiveBugDialogComponent,
    ReportTestTableComponent,
    RunnerCaseListComponent,
  ],
  providers: [TestlaufLiveFeedService, StepBugLinkService],
  templateUrl: './testlauf-runner-live.component.html',
  styleUrl: './testlauf-runner-live.component.scss',
})
export class TestlaufRunnerLiveComponent {
  private readonly feed = inject(TestlaufLiveFeedService);
  private readonly runService = inject(TestRunService);
  private readonly runnerService = inject(TestlaufRunnerService);
  private readonly bugLinks = inject(StepBugLinkService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private readonly ciMapping = inject(CiMappingService);
  private readonly route = inject(ActivatedRoute);

  private readonly currentLang = toSignal(this.translate.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.translate.currentLang,
  });

  readonly projectId = input.required<string>();
  readonly runId = input.required<string>();
  readonly state = input.required<RunnerState>();
  /** Die Quelle des Laufs; `null`, solange die Seite sie noch laedt. */
  readonly source = input<TestRunSource | null>(null);

  /** Ein Lauf auf einem verbundenen Rechner (TCDL) hat weder CI-Report noch Zuordnung (NEX-1672). */
  protected readonly showsCiReport = computed(() => {
    const source = this.source();
    return source !== null && source !== 'TCDL';
  });

  /** Der Durchlauf ist fertig — die Seite verlaesst den Runner. */
  readonly pipelineCompleted = output<void>();

  /** Der Durchlauf hat sich geaendert — die Seite holt den Stand neu (NEX-1236). */
  readonly staleState = output<void>();

  protected readonly executionId = computed(() => this.state().executionId);

  protected readonly unmatchedTests = computed(
    () => this.reportCounts()?.unmatched ?? this.state().unmatchedTests ?? 0,
  );
  protected readonly unmatchedTestsKey = computed(() =>
    this.unmatchedTests() === 1
      ? 'testRunner.live.pipeline.unmatchedTestsOne'
      : 'testRunner.live.pipeline.unmatchedTestsOther',
  );
  /** Fehlt bei einem Lauf ohne CI-Report ({@link showsCiReport}). */
  private readonly reportTable = viewChild(ReportTestTableComponent);
  private readonly reportTableHost = viewChild<ReportTestTableComponent, ElementRef<HTMLElement>>(
    ReportTestTableComponent,
    { read: ElementRef },
  );

  protected readonly matchingRuleId = `tlrl-matching-rule-${nextMatchingRuleId++}`;
  protected readonly matchingRuleOpen = signal(false);

  protected toggleMatchingRule(): void {
    this.matchingRuleOpen.update((open) => !open);
  }

  protected showUnmatchedTests(): void {
    const reportTable = this.reportTable();
    const table = this.reportTableHost()?.nativeElement;
    if (!reportTable || !table) return;
    reportTable.selectChip('unmatched');
    table.scrollIntoView({ behavior: 'smooth', block: 'start' });
    table.focus({ preventScroll: true });
  }

  private readonly rows = linkedSignal<RunnerState, LiveRow[]>({
    source: this.state,
    computation: (runnerState) => feedOrder(runnerState.cases.map(toRow)),
  });

  readonly feedRows = this.rows.asReadonly();

  /**
   * Die Faelle mit Schritten und Rechner-Auftrag, wie die Fallliste eines Rechner-Laufs sie
   * zeigt (NEX-1754). Der Feed schreibt sie fort; ein neu geladener Zustand setzt sie zurueck.
   */
  protected readonly cases = linkedSignal<RunnerState, RunnerCase[]>({
    source: this.state,
    computation: (runnerState) =>
      runnerState.cases.map((runnerCase) => withLiveJob(runnerCase, this.liveJobs.get(runnerCase.id))),
  });

  /** Der zuletzt live gemeldete Auftragsstand je Fall; ueberlebt das Neuladen (siehe {@link withLiveJob}). */
  private readonly liveJobs = new Map<string, LiveJobStatus>();

  protected readonly showsCaseList = computed(() => this.source() === 'TCDL');

  readonly connectionLost = computed(() => this.feed.connection() === 'lost');

  /**
   * Die Schritte, die die Pipeline dieses Durchlaufs ausgefuehrt hat (NEX-1334/1335).
   * Leer bei einem Durchlauf, der von Hand gelaufen ist — und solange das Backend das
   * Feld noch nicht fuehrt. Waehrend des Laufs ersetzt jedes `pipeline-steps-updated` die
   * Liste vollstaendig (NEX-1336); ein neu geladener Zustand setzt sie wieder auf seinen Stand.
   */
  readonly pipelineSteps = linkedSignal<RunnerState, RunnerPipelineStep[]>({
    source: this.state,
    computation: (runnerState) => runnerState.pipelineSteps ?? [],
  });

  private readonly hasFailedPipelineStep = computed(() =>
    this.pipelineSteps().some((step) => step.status === 'FAILED'),
  );
  /** Ob ein Rechner-Auftrag ueberhaupt einen Schritt bewertet hat; sonst nennt seine Karte den Grund. */
  readonly hasStepResults = computed(() => decidedSteps(this.state().cases).length > 0);

  readonly hasPipelineSteps = computed(() => this.pipelineSteps().length > 0);

  private readonly stepsBox = viewChild<ElementRef<HTMLElement>>('stepsBox');

  private readonly stepsScroll = signal(false);

  /**
   * Ein Tab-Halt auf der Schritt-Liste, aber nur solange sie seitlich laeuft. Steht die
   * Tabelle vollstaendig da, gibt es dort nichts zu bedienen — und ein Halt ohne Funktion
   * ist genau die zweite Station, gegen die die Entscheidung gegen ein {@code tabindex} je
   * Zeile steht.
   */
  readonly stepsTabIndex = computed(() => (this.stepsScroll() ? 0 : null));

  /**
   * Ob die Kopfzahl Faelle zaehlt: wo es welche gibt, oder wo der Durchlauf keine
   * Pipeline-Schritte zeigt.
   */
  private readonly countsCases = computed(() => this.feedRows().length > 0 || !this.hasPipelineSteps());

  /**
   * Was die Kopfzahl zaehlt: die Testfaelle, wo es welche gibt, sonst die
   * Pipeline-Schritte. Ein beobachteter Lauf traegt keine Faelle, und „0 von 0 Tests"
   * ueber einer gefuellten Schritt-Liste waere keine Zurueckhaltung, sondern falsch.
   */
  private readonly countedPoints = computed<{ status: ResultStatus }[]>(() =>
    this.countsCases() ? this.rows() : this.pipelineSteps(),
  );

  /** Gezaehlt wird, was auch dasteht — Tests oder Schritte. */
  readonly summaryKey = computed(() =>
    this.countsCases() ? 'testRunner.live.summary.tests' : 'testRunner.live.summary.steps',
  );

  /**
   * Die Punkt-Leiste des Durchlaufs, gefuellt aus seinen Schritten. Dieselbe Leiste wie
   * in der Live-Sektion und im Band der Uebersicht (NEX-1233) — und dieselbe Rechnung:
   * eine zweite Abbildung von Ergebnis auf Farbe liefe beim naechsten Statuswert von der
   * ersten fort.
   */
  private readonly stepProgress = computed<PipelineProgress>(() => ({
    segments: this.pipelineSteps().map((step) => ({ label: step.name, status: step.status })),
    status: this.state().status,
  }));

  readonly trackCells = computed<PipeTrackCell[]>(() => buildPipeTrackCells(this.stepProgress(), this.translate));

  /** Die Textalternative der Leiste — sie zaehlt Jobs, weil ihre Punkte welche sind. */
  readonly trackSummary = computed(() => buildPipeTrackSummary(this.trackCells(), 'JOBS', this.translate));

  /**
   * Die Zaehler der Report-Tests des Durchlaufs; {@code null}, solange keine da sind —
   * vor der ersten Antwort, bei einem Altlauf ohne Report-Daten und wenn das Laden
   * scheitert. Dann zaehlt die Ansicht wie bisher, statt Zahlen zu erfinden.
   */
  private readonly reportCounts = signal<ReportTestLiveCounts | null>(null);

  private countsRequest?: Subscription;

  /** Steigt mit jeder Meldung; eine Antwort, die vor ihr gelesen wurde, ist aelter als sie. */
  private countsVersion = 0;

  private readonly caseRollup = computed<Rollup>(() => {
    const points = this.countedPoints();
    const count = (status: ResultStatus) => points.filter((point) => point.status === status).length;
    const open = points.filter((point) => !isFinal(point.status)).length;
    return {
      total: points.length,
      passed: count('PASSED'),
      failed: count('FAILED'),
      skipped: count('SKIPPED'),
      blocked: count('BLOCKED'),
      notRun: 0,
      open,
      done: points.length - open,
    };
  });

  /**
   * Waehrend des Laufs misst der Balken an der angekuendigten Gesamtzahl, soweit die
   * Pipeline eine nennt; noch nicht gemeldete Tests stehen dann als offen da.
   */
  private readonly reportRollup = computed<Rollup | null>(() => {
    const report = this.reportCounts();
    if (!report) {
      return null;
    }
    const { passed, failed, skipped, blocked, notRun } = report.statusCounts;
    const done = passed + failed + skipped + blocked + notRun;
    const announced = this.live() ? report.reportedTotal : null;
    const total = announced === null ? report.total : Math.max(announced, report.total);
    return { total, passed, failed, skipped, blocked, notRun, open: Math.max(total - done, 0), done };
  });

  readonly rollup = computed<Rollup>(() => this.reportRollup() ?? this.caseRollup());

  /**
   * Die Zusammenfassung aus den Report-Tests; {@code null}, wo die Ansicht wie bisher zaehlt.
   * Blockierte und nicht ausgefuehrte Tests stehen nur da, wo es welche gibt.
   */
  readonly reportSummary = computed<SummaryPart[] | null>(() => {
    const report = this.reportCounts();
    const rollup = this.reportRollup();
    if (!report || !rollup) {
      return null;
    }
    const format = new Intl.NumberFormat(this.currentLang() || 'de');
    const part = (key: string, count: number, strong = false): SummaryPart => ({
      key,
      params: { count: format.format(count) },
      strong,
    });
    if (this.live()) {
      const head: SummaryPart =
        report.reportedTotal === null
          ? part(this.oneOrOther('testRunner.live.report.reported', report.total), report.total)
          : {
              key: 'testRunner.live.report.ofTotal',
              params: { done: format.format(rollup.done), total: format.format(rollup.total) },
              strong: false,
            };
      return rollup.failed > 0 ? [head, part('testRunner.live.report.failed', rollup.failed, true)] : [head];
    }
    return [
      part(this.oneOrOther('testRunner.live.report.total', report.total), report.total),
      part('testRunner.live.report.passed', rollup.passed),
      part('testRunner.live.report.failed', rollup.failed, rollup.failed > 0),
      part('testRunner.live.report.skipped', rollup.skipped),
      ...(rollup.blocked > 0 ? [part('testRunner.live.report.blocked', rollup.blocked)] : []),
      ...(rollup.notRun > 0 ? [part('testRunner.live.report.notRun', rollup.notRun)] : []),
      part('testRunner.live.report.matched', report.matched),
    ];
  });

  /** Ohne Pipeline-Karte steht die Pille in der Zusammenfassungszeile — aber nur, wo sie aus den Tests rechnet. */
  readonly summaryShowsPill = computed(() => !this.pipeline() && this.reportCounts() !== null);

  /** Die bislang fehlgeschlagenen Tests, zuletzt gemeldeter zuerst. */
  readonly failures = computed(() => this.rows().filter((row) => row.status === 'FAILED'));

  readonly failureTitleKey = computed(() =>
    this.failures().length === 1 ? 'testRunner.live.failures.titleOne' : 'testRunner.live.failures.titleOther',
  );

  readonly failureCountKey = computed(() =>
    this.failures().length === 1 ? 'testRunner.live.summary.failuresOne' : 'testRunner.live.summary.failuresOther',
  );

  private readonly bug = caseResultBugFlow();

  /**
   * Ob die Fehlerzeilen und die KI-Ursachenanalyse eine Anlage anbieten duerfen. Ohne Ziel
   * oder ohne Schreibrecht (das Anlegen verlangt TESTCASE_WRITE) waere der Knopf eine
   * Einladung in einen Fehler; die Begruendung, warum die leere Liste allein nicht
   * reicht, steht an {@link StepBugLinkService}.
   */
  readonly canCreateBug = computed(() => this.canAct() && this.bug.canCreate());

  readonly bugTargets = this.bug.targets;

  /**
   * Wofuer die empfohlene Aktion der KI-Ursachenanalyse den Bug anlegt (NEX-1548, Entscheidung 03.10.):
   * der juengste Fehlschlag der Ausfuehrung, an dem noch keiner haengt. Ohne ihn, ohne Ziel oder ohne
   * Schreibrecht entfaellt sie.
   */
  readonly kiBugFall = computed(() =>
    this.canCreateBug() ? (this.failures().find((row) => !row.linkedBugKey) ?? null) : null,
  );

  /** Die Ziel-Wahl gilt fuer alle Zeilen der Fehler-Karte; die Report-Tabelle merkt sich ihre eigene. */
  readonly bugTargetChoice = this.bug.choice;

  /** Der Bug am Durchlauf teilt die Ziel-Wahl der Fehler-Karte: beide legen fuer denselben Lauf an. */
  private readonly durchlaufBug = executionBugFlow(this.bug.choice);

  /**
   * Der Dialog haelt die Zeile, fuer die er geoeffnet wurde, als Schnappschuss: der Feed
   * sortiert die Liste weiter um, und ein Neuladen baut sie neu auf.
   */
  readonly bugDialog = this.bug.dialog;
  readonly bugPreview = this.bug.preview;

  /** Was der Dialog zeigt — fuer einen Fall oder fuer den Durchlauf; offen ist hoechstens einer. */
  readonly bugDialogContext = computed(() => this.bugDialog()?.context ?? this.durchlaufBug.dialog()?.context ?? null);
  readonly bugSubmitting = computed(() => this.bug.submitting() || this.durchlaufBug.submitting());
  readonly bugError = computed(() => this.bug.error() ?? this.durchlaufBug.error());

  /** Der Vorgang, den Zeile oder Durchlauf des Dialogs inzwischen traegt — etwa nach einem 409 neu geladen. */
  readonly bugDialogExistingKey = computed(() => {
    const dialog = this.bugDialog();
    if (dialog) {
      return this.rows().find((row) => row.caseResultId === dialog.caseResultId)?.linkedBugKey ?? null;
    }
    const durchlauf = this.durchlaufBug.dialog();
    return durchlauf ? this.durchlaufBugKey(durchlauf.executionId) : null;
  });

  readonly bugDialogExistingUrl = computed(() => {
    const key = this.bugDialogExistingKey();
    const id = this.bugDialog()?.caseResultId ?? this.durchlaufBug.dialog()?.executionId;
    return key && id ? this.bug.urlFor(id, key) : null;
  });

  /** Die Pipeline ist fertig, waehrend der Dialog stand — die Seite wird erst danach verlassen. */
  private completedWhileDialogOpen = false;

  readonly live = computed(() => this.state().live);

  /**
   * Der CI-Lauf hinter dem Durchlauf, {@code null} wo es keinen gibt.
   *
   * <p>Ohne Build-Nummer (nach dem Ausloesen ueber NEX-752 kommt sie erst, wenn der
   * Beobachter den Lauf sieht, NEX-1290/NEX-1293) steht der Pipeline-Name allein,
   * statt ein Trennzeichen ins Leere zu setzen.
   */
  readonly pipeline = computed<PipelineCard | null>(() => {
    const linked = this.state().pipeline;
    if (!linked) {
      return null;
    }
    return {
      provider: PIPELINE_PROVIDERS[linked.provider] ?? UNKNOWN_PROVIDER,
      label: linked.buildNumber ? `${linked.pipeline} · #${linked.buildNumber}` : linked.pipeline,
      runUrl: linked.runUrl,
      logUrl: linked.logUrl,
    };
  });

  /** Was der Bug-Dialog vom Durchlauf vorbelegt — fuer die Fehler-Karte und die Report-Tabelle. */
  readonly bugRun = computed<CaseResultBugRun>(() => ({
    runKey: this.state().runKey,
    executionNr: this.state().nr,
    pipeline: this.pipeline()?.label ?? null,
  }));

  /**
   * Ein Lauf ohne Report wird mit dem Ausgang seiner Jobs abgeschlossen (NEX-1331/NEX-1334): dann traegt
   * nur der Status das Scheitern. Pipeline-Schritte zaehlen nicht als Testergebnisse — ein roter
   * Build-Schritt ist kein roter Test.
   */
  private readonly gescheitertOhneTestergebnisse = computed(
    () => GESCHEITERT.has(this.state().status) && this.rows().length === 0 && (this.reportCounts()?.total ?? 0) === 0,
  );

  readonly pipelinePill = computed<PipelinePill>(() => {
    if (this.live()) {
      return this.hasFailedPipelineStep()
        ? { status: 'failed', label: 'testRunner.live.pipeline.red', pulsing: false }
        : { status: 'running', label: 'testRunner.live.pipeline.running', pulsing: true };
    }
    const report = this.reportCounts();
    if (report && report.total > 0 && report.statusCounts.skipped === report.total) {
      return { status: 'skipped', label: 'testRunner.live.pipeline.skipped', pulsing: false };
    }
    return this.rollup().failed > 0 || this.gescheitertOhneTestergebnisse()
      ? { status: 'failed', label: 'testRunner.live.pipeline.red', pulsing: false }
      : { status: 'passed', label: 'testRunner.live.pipeline.green', pulsing: false };
  });

  private readonly now = signal(Date.now());

  /** Die Laufzeit des Durchlaufs; {@code null}, sobald er nicht mehr laeuft. */
  readonly elapsed = computed(() => {
    if (!this.live()) {
      return null;
    }
    const startedAt = Date.parse(this.state().startedAt);
    return Number.isNaN(startedAt) ? null : formatElapsedClock(this.now() - startedAt);
  });

  /**
   * Ob die Pipeline-Aktionen ueberhaupt erscheinen. Die Antwort kommt vom Server
   * (NEX-1236): {@code TESTCASE_WRITE} haengt an der Projektmitgliedschaft und laesst
   * sich hier nicht nachbauen.
   */
  readonly canAct = computed(() => this.state().callerCanWrite);

  /**
   * Welcher Knopf steht, entscheidet der Zustand des Durchlaufs und nicht der des Laufs
   * (Design-Bundle {@code fc36ea78fc0}, {@code page-testlauf.jsx:455-460}): ein laufender
   * laesst sich abbrechen, ein beendeter erneut ausloesen.
   */
  readonly canCancel = computed(() => this.canAct() && this.live());
  readonly canRerun = computed(() => this.canAct() && !this.live());

  /** Laeuft gerade eine Aktion — beide Knoepfe stehen still, bis die Antwort da ist. */
  private readonly acting = signal(false);
  readonly actionBusy = this.acting.asReadonly();

  /** Die Rueckfrage vor dem Abbruch; abgebrochen wird erst nach ihrer Bestaetigung. */
  private readonly confirming = signal(false);
  readonly cancelConfirmVisible = this.confirming.asReadonly();

  private readonly failedAction = signal<PipelineActionError | null>(null);
  readonly actionError = this.failedAction.asReadonly();

  readonly pillStatus = runnerPillStatus;
  readonly resultKey = runnerResultKey;

  /**
   * Der Durchlauf aus der Liste des Laufs (NEX-1548): nur sie traegt Fehlerkategorie, Job,
   * Schritt und Zeile. {@code null}, solange sie nicht geladen ist oder das Laden scheitert.
   */
  private readonly befund = signal<TestRunExecution | null>(null);

  private befundRequest?: Subscription;

  private pipelineStepsRequest?: Subscription;

  /** Der Ursachen-Block steht, sobald die Runde eine Kategorie hat — auch wenn sie noch auf ihren Report wartet. */
  readonly ursache = computed(() => {
    const befund = this.befund();
    return befund && istBekannteKategorie(befund.fehlerKategorie) ? befund : null;
  });

  /** Solange die Pipeline laeuft und keine Ursache feststeht, kuendigt ein Hinweis die Analyse an. */
  readonly ursacheHinweis = computed(() => this.live() && this.pipeline() !== null && this.ursache() === null);

  /** Absprung aus dem Postfach (NEX-1548 AK11); ob die Anfrage zur Ausfuehrung passt, prueft der Block. */
  readonly kiAnfrageId = toSignal(this.route.queryParamMap.pipe(map((params) => params.get('kiAnfrage'))), {
    initialValue: null,
  });

  /**
   * Fuer welchen Durchlauf die empfohlene Aktion den Bug an ihm selbst anlegt: nur in einer
   * Kategorie ohne gescheiterte Tests, solange keiner haengt, mit Ziel und Schreibrecht. Hat der Lauf rote
   * Tests — als Fall oder im Report —, gehoeren die Bugs dorthin, auch wenn sie dort schon alle haengen.
   */
  readonly kiBugDurchlauf = computed(() => {
    const befund = this.ursache();
    if (!befund || !this.canCreateBug() || befund.linkedBugKey || this.hatRoteTests()) {
      return null;
    }
    return befund.fehlerKategorie && BUG_AM_DURCHLAUF.has(befund.fehlerKategorie) ? befund : null;
  });

  /**
   * Die Report-Zaehler und nicht {@link rollup}: ohne Report zaehlt der die Pipeline-Schritte, und ein
   * roter Build-Schritt ist kein roter Test.
   */
  private readonly hatRoteTests = computed(
    () => this.failures().length > 0 || (this.reportCounts()?.statusCounts.failed ?? 0) > 0,
  );

  readonly kiAktionAnbieten = computed(() => this.kiBugFall() !== null || this.kiBugDurchlauf() !== null);

  /** Der am Durchlauf verknuepfte Bug; der Absprung entsteht wie bei einem Fall-Bug. */
  readonly durchlaufBugLink = computed<KiPipelineVerknuepfterBug | null>(() => {
    const befund = this.ursache();
    const key = befund?.linkedBugKey;
    return befund && key ? { key, url: this.durchlaufBug.urlFor(befund.id, key) } : null;
  });

  /** Die Zeile meint das Job-Log; ohne dessen Adresse fuehrt der Link auf den Lauf. */
  readonly ursacheCiUrl = computed(() => {
    const card = this.pipeline();
    return card ? (card.logUrl ?? card.runUrl) : null;
  });

  constructor() {
    this.feed.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => this.apply(event));
    this.destroyRef.onDestroy(() => this.feed.disconnect());
    // Am Durchlauf und nicht am ganzen Zustand: ein Neuladen desselben Durchlaufs
    // darf die Verbindung nicht abreissen, ein Wechsel auf einen anderen muss sie
    // umhaengen — sonst liefe der Feed auf dem Topic des vorigen weiter.
    effect(() => {
      const executionId = this.executionId();
      const projectId = untracked(this.projectId);
      const runId = untracked(this.runId);
      this.feed.disconnect();
      this.feed.connect(projectId, runId, executionId);
    });
    effect((onCleanup) => {
      if (!this.live()) {
        return;
      }
      const handle = setInterval(() => this.now.set(Date.now()), CLOCK_TICK_MS);
      onCleanup(() => clearInterval(handle));
    });
    // Gemessen statt angenommen: ob die Schritt-Tabelle seitlich laeuft, haengt an der
    // Breite des Fensters und an den Namen, die der Anbieter meldet.
    effect((onCleanup) => {
      const box = this.stepsBox()?.nativeElement;
      if (!box) {
        this.stepsScroll.set(false);
        return;
      }
      const measure = (): void => this.stepsScroll.set(box.scrollWidth > box.clientWidth);
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(box);
      onCleanup(() => observer.disconnect());
    });
    // Im Effect statt im Konstruktor: `projectId` ist ein Pflicht-Input und steht
    // dort noch nicht.
    effect(() => this.bugLinks.load(this.projectId()));
    effect(() => {
      this.executionId();
      untracked(() => {
        this.reportCounts.set(null);
        this.loadCounts();
      });
    });
    effect(() => {
      this.executionId();
      untracked(() => {
        this.befund.set(null);
        this.loadBefund();
      });
    });
    this.reloadAfterReconnect();
    this.destroyRef.onDestroy(() => this.countsRequest?.unsubscribe());
    this.destroyRef.onDestroy(() => this.befundRequest?.unsubscribe());
    this.destroyRef.onDestroy(() => this.pipelineStepsRequest?.unsubscribe());
  }

  stepDuration(step: RunnerPipelineStep): string {
    return formatDurationMs(step.durationMs);
  }

  /** Das Ziel des Absprungs — woher es stammt, entscheidet {@link StepBugLinkService}. */
  bugUrl(row: LiveRow): string | null {
    return this.bug.urlFor(row.caseResultId, row.linkedBugKey ?? '');
  }

  /** Oeffnet den Dialog zur Zeile; angelegt wird erst mit seiner Bestaetigung. */
  openBugDialog(row: LiveRow): void {
    if (row.linkedBugKey) {
      return;
    }
    this.openBugFor(row);
  }

  /**
   * „An Entwicklung geben“ und „Bug anlegen“ fuehren in denselben Dialog, vorbelegt mit dem Text der Analyse:
   * fuer den juengsten Fall ohne Bug, sonst fuer den Durchlauf selbst.
   */
  openKiBugDialog({ kategorie, zusammenfassung, ursache }: KiPipelineAktionVorlage): void {
    const kiAnalyse = { kategorie, zusammenfassung, ursache };
    const row = this.kiBugFall();
    if (row) {
      this.openBugFor(row, kiAnalyse);
      return;
    }
    const durchlauf = this.kiBugDurchlauf();
    if (durchlauf?.fehlerKategorie) {
      this.durchlaufBug.open(this.projectId(), this.runId(), durchlauf.id, {
        autoId: '',
        testName: '',
        ...this.bugRun(),
        kiAnalyse,
        ausfuehrung: { kategorie: durchlauf.fehlerKategorie, anhang: null },
      });
    }
  }

  private openBugFor(row: LiveRow, kiAnalyse?: LiveBugKiAnalyse): void {
    this.bug.open(this.projectId(), row.caseResultId, {
      autoId: row.autoId,
      testName: row.name,
      ...this.bugRun(),
      ...(kiAnalyse ? { kiAnalyse } : {}),
    });
  }

  /**
   * Haengt an der Zeile inzwischen ein Vorgang — angelegt oder nach einem 409 neu geladen —,
   * ist die Aktion, von der der Dialog kam, weg; der Fokus geht dann auf den Absprung.
   */
  closeBugDialog(): void {
    const linked = this.bugDialogExistingKey() !== null;
    const dialog = this.bug.close();
    const durchlauf = dialog ? null : this.durchlaufBug.close();
    if (!dialog && !durchlauf) {
      return;
    }
    if (linked) {
      this.focusBugLink(
        dialog
          ? `[data-testid="live-failure-bug-link"][data-case-result="${CSS.escape(dialog.caseResultId)}"]`
          : '[data-testid="ursache-bug-link"]',
      );
    }
    if (this.completedWhileDialogOpen) {
      this.completedWhileDialogOpen = false;
      this.pipelineCompleted.emit();
    }
  }

  /**
   * Legt den Vorgang aus dem Fall-Ergebnis an und verknuepft ihn. Schluessel und
   * Adresse kommen vom Server. Eine Absage laesst den Dialog mit seinen Eingaben stehen;
   * ein 409 heisst, dass am Fall schon ein Vorgang haengt — den bringt der neue Stand.
   */
  confirmBug(request: CreateBugFromStepResultRequest): void {
    if (this.durchlaufBug.dialog()) {
      this.confirmDurchlaufBug(request);
      return;
    }
    this.bug.confirm(this.projectId(), request, {
      created: (caseResultId, key) => {
        this.markBugLinked(caseResultId, key);
        this.reportTable()?.markBugLinked(caseResultId, key);
        this.closeBugDialog();
      },
      alreadyLinked: () => this.staleState.emit(),
    });
  }

  /** Ein 409 heisst, dass am Durchlauf schon ein Vorgang haengt — den bringt die neu geladene Liste. */
  private confirmDurchlaufBug(request: CreateBugFromStepResultRequest): void {
    this.durchlaufBug.confirm(this.projectId(), request, {
      created: (executionId, key) => {
        this.befund.update((befund) => (befund?.id === executionId ? { ...befund, linkedBugKey: key } : befund));
        this.closeBugDialog();
      },
      alreadyLinked: () => this.loadBefund(),
    });
  }

  private durchlaufBugKey(executionId: string): string | null {
    const befund = this.befund();
    return befund?.id === executionId ? (befund.linkedBugKey ?? null) : null;
  }

  /** Eine Anlage aus der Report-Tabelle gilt auch fuer die Fehler-Karte, die daneben steht. */
  markBugLinked(caseResultId: string, key: string): void {
    this.rows.update((rows) =>
      rows.map((entry) => (entry.caseResultId === caseResultId ? { ...entry, linkedBugKey: key } : entry)),
    );
  }

  private focusBugLink(selector: string): void {
    afterNextRender(() => this.host.nativeElement.querySelector<HTMLElement>(selector)?.focus(), {
      injector: this.injector,
    });
  }

  /** Fragt vor dem Abbruch nach: ein laufender Lauf endet damit, und das ist nicht umkehrbar. */
  askCancel(): void {
    this.failedAction.set(null);
    this.confirming.set(true);
  }

  dismissCancel(): void {
    this.confirming.set(false);
  }

  /**
   * Bricht den Durchlauf ab. Der Stand kommt danach vom Server und nicht aus der Antwort:
   * mit dem Lauf endet auch, was gerade noch an Ergebnissen eingelaufen ist, und der
   * Runner zeigt beides an derselben Stelle.
   */
  confirmCancel(): void {
    if (this.acting()) {
      return;
    }
    this.acting.set(true);
    this.runService
      .cancelPipelineExecution(this.projectId(), this.runId(), this.state().executionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.acting.set(false);
          this.confirming.set(false);
          this.staleState.emit();
        },
        error: (error: HttpErrorResponse) => this.failAction(error),
      });
  }

  /**
   * Loest die Pipeline erneut aus und oeffnet den Durchlauf, den das Ausloesen angelegt
   * hat (NEX-752). Ohne Rueckfrage: ein zusaetzlicher Durchlauf nimmt nichts weg, im
   * Unterschied zum Abbruch.
   */
  rerun(): void {
    if (this.acting()) {
      return;
    }
    this.acting.set(true);
    this.failedAction.set(null);
    this.runService
      .triggerPipeline(this.projectId(), this.runId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (triggered) => {
          this.acting.set(false);
          void this.router.navigate([
            '/projects',
            this.projectId(),
            'test-runs',
            this.runId(),
            'executions',
            triggered.executionId,
          ]);
        },
        error: (error: HttpErrorResponse) => this.failAction(error),
      });
  }

  /**
   * Die Absage steht als Meldung an der Karte und nicht nur als Kurzhinweis: sie nennt
   * einen Grund, an dem etwas zu tun ist, und ein Schnipsel, der nach Sekunden
   * verschwindet, nimmt das wieder weg.
   */
  private failAction(error: HttpErrorResponse): void {
    this.acting.set(false);
    this.confirming.set(false);
    this.failedAction.set(pipelineActionError(error));
  }

  /**
   * Die Zaehler kommen aus derselben Abfrage wie die Tabelle, aber als eigene: die Tabelle
   * filtert und blaettert, die Kopfzeile zaehlt immer den ganzen Durchlauf.
   */
  protected loadCounts(): void {
    this.countsRequest?.unsubscribe();
    const version = this.countsVersion;
    this.countsRequest = this.ciMapping
      .list(this.projectId(), this.runId(), {
        executionId: this.executionId(),
        filter: 'all',
        page: 0,
        size: COUNTS_PAGE_SIZE,
      })
      .subscribe({
        next: (page) => {
          if (version !== this.countsVersion || !page.hasReportData) {
            return;
          }
          this.reportCounts.set({
            statusCounts: page.statusCounts,
            total: page.total,
            matched: page.matched,
            unmatched: page.unmatched,
            reportedTotal: page.reportedTotal,
          });
        },
        error: () => undefined,
      });
  }

  /** Was waehrend einer Trennung gemeldet wurde, kommt nicht nach; nur die Abfrage kennt es. */
  private reloadAfterReconnect(): void {
    let lost = false;
    effect(() => {
      const connection = this.feed.connection();
      if (connection === 'lost') {
        lost = true;
      } else if (connection === 'connected' && lost) {
        lost = false;
        untracked(() => {
          this.loadCounts();
          this.loadBefund();
          this.loadPipelineSteps();
        });
      }
    });
  }

  /** Nur die Schritte: ein ganzer neuer Zustand setzte auch die Fall-Zeilen zurueck. */
  private loadPipelineSteps(): void {
    this.pipelineStepsRequest?.unsubscribe();
    this.pipelineStepsRequest = this.runnerService
      .getState(this.projectId(), this.runId(), this.executionId())
      .subscribe({
        next: (fresh) => this.setPipelineSteps(fresh.pipelineSteps ?? []),
        error: () => undefined,
      });
  }

  private setPipelineSteps(steps: RunnerPipelineStep[]): void {
    this.pipelineSteps.set([...steps].sort((a, b) => a.nr - b.nr));
  }

  private applyReportCounts(event: TestRunLiveEvent): void {
    if (event.executionId !== this.executionId()) {
      return;
    }
    if (event.type === 'report-tests-replaced') {
      this.loadCounts();
    } else if (event.type === 'report-test-updated' && event.counts) {
      this.countsVersion++;
      this.reportCounts.set(event.counts);
    }
  }

  /**
   * Holt den Befund des Durchlaufs beim Oeffnen der Seite und nach jeder Meldung, dass sich seine
   * Fehlerkategorie geaendert hat (NEX-1551c). Eine neue Abfrage loest die laufende ab; bis zur
   * Antwort und nach einer Absage bleibt der bisherige Befund stehen.
   */
  private loadBefund(): void {
    this.befundRequest?.unsubscribe();
    const executionId = this.executionId();
    this.befundRequest = this.runService.listExecutions(this.projectId(), this.runId()).subscribe({
      next: (executions) => this.befund.set(executions.find((entry) => entry.id === executionId) ?? null),
      error: () => undefined,
    });
  }

  private oneOrOther(prefix: string, count: number): string {
    return count === 1 ? `${prefix}One` : `${prefix}Other`;
  }

  /**
   * Schreibt die Fallliste fort. Ein Fehler bei der Ausfuehrung laedt den Zustand neu, weil
   * erst der den Fehlertext des Auftrags traegt.
   */
  private applyToCases(event: TestRunLiveEvent): void {
    const caseResultId = event.caseResultId;
    if (
      event.executionId !== this.executionId() ||
      !caseResultId ||
      !this.cases().some((runnerCase) => runnerCase.id === caseResultId)
    ) {
      return;
    }
    const update = (change: (runnerCase: RunnerCase) => RunnerCase) =>
      this.cases.update((cases) =>
        cases.map((runnerCase) => (runnerCase.id === caseResultId ? change(runnerCase) : runnerCase)),
      );
    const status = event.status;
    const jobStatus = event.jobStatus;
    if (event.type === 'step-updated' && event.stepResultId && status) {
      update((runnerCase) => ({
        ...runnerCase,
        steps: runnerCase.steps.map((step) =>
          step.id === event.stepResultId ? { ...step, actualStatus: status } : step,
        ),
      }));
    } else if (event.type === 'test-completed' && status) {
      update((runnerCase) => ({ ...runnerCase, status, completedAt: event.at }));
    } else if (event.type === 'runner-job-updated' && jobStatus) {
      const live: LiveJobStatus = { status: jobStatus, at: event.at };
      const known = this.cases().find((runnerCase) => runnerCase.id === caseResultId)?.runnerJob !== undefined;
      this.liveJobs.set(caseResultId, live);
      update((runnerCase) => withLiveJob(runnerCase, live));
      // Ohne Auftrag am Fall fehlen Rechner und Zeiten, bei ERROR der Fehlertext: beides bringt erst das Neuladen.
      if (jobStatus === 'ERROR' || !known) {
        this.staleState.emit();
      }
    }
  }

  /** Ein Schritt-Ereignis aendert Rollup und Fehler-Karte nicht; das Ergebnis meldet der Fall selbst mit {@code test-completed}. */
  private apply(event: TestRunLiveEvent): void {
    this.applyReportCounts(event);
    this.applyToCases(event);
    if (event.type === 'pipeline-failure-categorized') {
      if (event.executionId === this.executionId()) {
        this.loadBefund();
      }
      return;
    }
    if (event.type === 'pipeline-steps-updated') {
      if (event.executionId === this.executionId() && event.pipelineSteps) {
        this.pipelineStepsRequest?.unsubscribe();
        this.setPipelineSteps(event.pipelineSteps);
      }
      return;
    }
    if (event.type === 'pipeline-completed') {
      if (this.bugDialogContext()) {
        this.completedWhileDialogOpen = true;
      } else {
        this.pipelineCompleted.emit();
      }
      return;
    }
    const caseResultId = event.caseResultId;
    if (!caseResultId || !this.rows().some((row) => row.caseResultId === caseResultId)) {
      return;
    }
    if (event.type !== 'test-completed' || !event.status) {
      return;
    }
    const status = event.status;
    this.rows.update((rows) =>
      feedOrder(rows.map((row) => (row.caseResultId === caseResultId ? { ...row, status, at: event.at } : row))),
    );
  }
}
